import React, { useState, useRef, useEffect, cloneElement } from 'react';
import { createPortal } from 'react-dom';

/**
 * Tooltip - Composant qui affiche une bulle d'aide via React Portal
 * pour échapper aux conteneurs avec `overflow: hidden`.
 *
 * Usage:
 *   <Tooltip content="Mon texte d'aide">
 *     <button>Hover me</button>
 *   </Tooltip>
 *
 * Le tooltip est positionné automatiquement au-dessus de l'élément.
 * Si l'espace manque en haut, il bascule en dessous.
 */
const Tooltip = ({ children, content, placement = 'top', delay = 100, multiline = false }) => {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, isAbove: true });
  const wrapperRef = useRef(null);
  const showTimer = useRef(null);

  const computePosition = () => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const tooltipWidth = multiline ? 280 : 200;
    const tooltipHeight = multiline ? 56 : 28;

    // Centrer horizontalement, recadrer si dépasse
    let left = rect.left + rect.width / 2;
    const halfTip = tooltipWidth / 2;
    if (left - halfTip < 8) left = halfTip + 8;
    if (left + halfTip > window.innerWidth - 8) left = window.innerWidth - halfTip - 8;

    // Au-dessus par défaut, sinon en-dessous
    const spaceAbove = rect.top;
    const isAbove = spaceAbove > tooltipHeight + 12 || placement === 'top';
    const top = isAbove ? rect.top - 6 : rect.bottom + 6;

    setPos({ top, left, isAbove });
  };

  const handleEnter = () => {
    if (showTimer.current) clearTimeout(showTimer.current);
    showTimer.current = setTimeout(() => {
      computePosition();
      setVisible(true);
    }, delay);
  };

  const handleLeave = () => {
    if (showTimer.current) clearTimeout(showTimer.current);
    setVisible(false);
  };

  useEffect(() => {
    return () => {
      if (showTimer.current) clearTimeout(showTimer.current);
    };
  }, []);

  // Cloner l'enfant pour attacher les listeners + ref
  const child = cloneElement(children, {
    ref: wrapperRef,
    onMouseEnter: (e) => {
      handleEnter();
      if (children.props.onMouseEnter) children.props.onMouseEnter(e);
    },
    onMouseLeave: (e) => {
      handleLeave();
      if (children.props.onMouseLeave) children.props.onMouseLeave(e);
    },
    onFocus: (e) => {
      handleEnter();
      if (children.props.onFocus) children.props.onFocus(e);
    },
    onBlur: (e) => {
      handleLeave();
      if (children.props.onBlur) children.props.onBlur(e);
    },
  });

  return (
    <>
      {child}
      {visible &&
        content &&
        createPortal(
          <div
            className={`portal-tooltip ${pos.isAbove ? 'tip-above' : 'tip-below'} ${multiline ? 'tip-multi' : ''}`}
            style={{
              top: pos.top,
              left: pos.left,
              transform: pos.isAbove
                ? 'translate(-50%, -100%)'
                : 'translate(-50%, 0)',
            }}
            role="tooltip"
          >
            {content}
          </div>,
          document.body
        )}
    </>
  );
};

export default Tooltip;
